import React, { useEffect, useState, useCallback, useRef } from 'react';
import { motion } from 'framer-motion';
import { Sparkles, Check, Loader2, X, RotateCcw, Crown, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import {
  isNativeIAPSupported, getOfferings, purchasePackage, restorePurchases, syncPremiumWithBackend,
} from '../lib/revenuecat';
import { isAndroidNative } from '../utils/platform';
import { useLanguage } from '../App';

const SUPPORT_EMAIL = 'support@aayi.ai';

// Error copy keyed by kind so users can tell "still setting up" from a real
// failure, instead of a dead-end message.
const ERROR_COPY = {
  platform: {
    title: 'Native app only',
    body: 'In-app purchases are only available in the Aayi iOS or Android app.',
    retry: false,
  },
  setup: {
    title: 'Plans are on their way',
    body: 'Premium plans are being set up and will be available shortly. Please try again in a little while.',
    retry: true,
  },
  load: {
    title: "Couldn't load plans",
    body: "We couldn't reach the store. Check your connection and try again.",
    retry: true,
  },
};

/**
 * Native IAP paywall (iOS App Store + Android Google Play Billing).
 * Both platforms are routed through RevenueCat which returns the correct
 * platform-specific offerings from the RC dashboard. Web users get redirected
 * to the existing Stripe /subscription page by the caller.
 *
 * Store rules honored:
 *  - Apple 3.1.1 (Restore Purchases button on any paywall with non-consumables)
 *  - Apple 3.1.2(c) (title, length, price, EULA + Privacy links, auto-renewal disclosure)
 *  - Play Policy §4.5 (uses Play Billing, no external links to payments)
 */
const NativePaywall = ({ open, onClose, onSuccess }) => {
  const { t } = useLanguage();
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [errKind, setErrKind] = useState(null);
  const mounted = useRef(true);

  const loadOfferings = useCallback(async () => {
    if (!isNativeIAPSupported()) {
      if (mounted.current) { setErrKind('platform'); setLoading(false); }
      return;
    }
    try {
      if (mounted.current) { setLoading(true); setErrKind(null); }
      const offering = await getOfferings();
      const pkgs = offering?.availablePackages || [];
      if (!mounted.current) return;
      setPackages(pkgs);
      setErrKind(pkgs.length === 0 ? 'setup' : null); // reached store, but nothing configured yet
    } catch (e) {
      if (mounted.current) setErrKind('load'); // couldn't reach the store
    } finally {
      if (mounted.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!open) return;
    mounted.current = true;
    loadOfferings();
    return () => { mounted.current = false; };
  }, [open, loadOfferings]);

  const handleBuy = async (pkg) => {
    try {
      setBusy(true);
      await purchasePackage(pkg);
      const result = await syncPremiumWithBackend();
      if (result?.is_premium) {
        toast.success('Welcome to Aayi Premium! ✨');
        onSuccess?.();
        onClose?.();
      } else {
        toast.success('Purchase complete — refreshing status...');
        setTimeout(() => { onSuccess?.(); onClose?.(); }, 1200);
      }
    } catch (err) {
      if (err?.userCancelled || err?.code === '1') {
        // User cancelled — silent
      } else {
        toast.error(err?.message || 'Purchase failed. Please try again.');
      }
    } finally { setBusy(false); }
  };

  const handleRestore = async () => {
    try {
      setBusy(true);
      await restorePurchases();
      const result = await syncPremiumWithBackend();
      if (result?.is_premium) {
        toast.success('Purchases restored!');
        onSuccess?.();
        onClose?.();
      } else {
        toast.info('No active subscription found on this Apple ID.');
      }
    } catch (e) {
      toast.error('Could not restore purchases.');
    } finally { setBusy(false); }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[200] flex items-end sm:items-center justify-center bg-black/60 backdrop-blur-sm" data-testid="apple-paywall">
      <motion.div
        initial={{ y: 60, opacity: 0 }} animate={{ y: 0, opacity: 1 }}
        className="relative w-full sm:max-w-md bg-white rounded-t-3xl sm:rounded-3xl p-6 max-h-[90vh] overflow-y-auto">
        <button onClick={onClose} disabled={busy} className="absolute top-4 right-4 w-8 h-8 rounded-full bg-[#FAF7F2] flex items-center justify-center" data-testid="paywall-close-btn">
          <X className="w-4 h-4 text-[#57534E]" />
        </button>

        <div className="flex items-center gap-2 mb-2">
          <div className="w-10 h-10 rounded-2xl bg-gradient-to-br from-[#E05A47] to-[#F4B333] flex items-center justify-center">
            <Crown className="w-5 h-5 text-white" />
          </div>
          <h2 className="text-2xl font-black text-[#1C1917]" style={{ fontFamily: 'Outfit' }}>{t('aayiPremium')}</h2>
        </div>
        <p className="text-sm text-[#57534E] mb-6">{t('unlockUnlimited')}</p>

        <ul className="space-y-2 mb-6">
          {[
            'Unlimited AI Saheli voice conversations',
            'Unlimited cry decoder analyses',
            'Priority doctor video consults',
            'Smart vaccination + growth reminders',
            'Ad-free experience',
          ].map(b => (
            <li key={b} className="flex items-start gap-2 text-sm text-[#1C1917]">
              <Check className="w-4 h-4 text-[#3D9970] flex-shrink-0 mt-0.5" />
              <span>{b}</span>
            </li>
          ))}
        </ul>

        {loading ? (
          <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-[#E05A47]" /></div>
        ) : errKind ? (
          <div className="text-center py-2" data-testid="paywall-error">
            <p className="text-base font-bold text-[#1C1917] mb-1" style={{ fontFamily: 'Outfit' }}>
              {ERROR_COPY[errKind]?.title}
            </p>
            <p className="text-sm text-[#57534E] mb-4">{ERROR_COPY[errKind]?.body}</p>
            {ERROR_COPY[errKind]?.retry && (
              <button onClick={loadOfferings} disabled={busy}
                data-testid="paywall-retry-btn"
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#E05A47] text-white text-sm font-bold hover:-translate-y-0.5 transition-transform disabled:opacity-60 active:scale-95">
                <RefreshCw className="w-4 h-4" /> Try again
              </button>
            )}
            <p className="mt-4 text-[11px] text-[#A8A29E]">
              Still stuck?{' '}
              <a href={`mailto:${SUPPORT_EMAIL}?subject=Aayi%20Premium%20-%20plans%20not%20loading`}
                data-testid="paywall-support-link"
                className="underline text-[#57534E] font-semibold">{t('contactSupport')}</a>
            </p>
          </div>
        ) : (
          <div className="space-y-3" data-testid="paywall-packages">
            {packages.map(pkg => {
              const product = pkg.product || {};
              const identifier = product.identifier || pkg.identifier || '';
              const isLifetime = identifier.includes('lifetime');
              const isYearly   = identifier.includes('yearly') || identifier.includes('annual');
              const isMonthly  = identifier.includes('monthly');
              // Apple 3.1.2(c) requires clear length + per-unit price for auto-renewables
              const productTitle = isLifetime
                ? 'Aayi Pro — Lifetime Access'
                : isYearly
                  ? 'Aayi Pro — Yearly Auto-Renewable Subscription'
                  : isMonthly
                    ? 'Aayi Pro — Monthly Auto-Renewable Subscription'
                    : (product.title || 'Aayi Pro');
              const length = isLifetime ? 'One-time purchase' : (isMonthly ? '1 month, auto-renewing' : '1 year, auto-renewing');
              // Best-effort per-unit price (RevenueCat gives priceString + price float)
              let perUnit = null;
              if (isYearly && product.price) {
                const monthly = product.price / 12;
                const cur = product.currencyCode || '';
                perUnit = `Equivalent to about ${cur} ${monthly.toFixed(2)} per month`;
              }
              return (
                <button key={pkg.identifier} onClick={() => handleBuy(pkg)} disabled={busy}
                  data-testid={`paywall-buy-${identifier}`}
                  className={`w-full p-4 rounded-2xl border-2 text-left transition-all ${isLifetime ? 'border-[#F4B333] bg-gradient-to-br from-[#FFF8EC] to-white' : 'border-[#E05A47] bg-gradient-to-br from-[#FFF5F3] to-white'} hover:-translate-y-0.5 disabled:opacity-60 disabled:translate-y-0 active:scale-[0.98]`}>
                  <div className="flex items-center justify-between mb-1">
                    <p className="font-bold text-[#1C1917] text-sm leading-tight" style={{ fontFamily: 'Outfit' }}>
                      {productTitle}
                    </p>
                    {isYearly && (
                      <span className="px-2 py-0.5 rounded-full bg-[#E05A47] text-white text-[9px] font-bold uppercase tracking-wider flex-shrink-0 ml-2">{t('bestValue')}</span>
                    )}
                  </div>
                  <p className="text-xl font-black text-[#1C1917] mb-0.5">{product.priceString || ''}</p>
                  {perUnit && <p className="text-[11px] text-[#78716C]">{perUnit}</p>}
                  <p className="text-[11px] text-[#78716C] mt-1">
                    <b>{t('lengthLabel')}</b> {length}
                  </p>
                </button>
              );
            })}
          </div>
        )}

        <div className="mt-5 flex items-center justify-between gap-3">
          <button onClick={handleRestore} disabled={busy || loading}
            data-testid="paywall-restore-btn"
            className="text-xs font-bold text-[#57534E] underline flex items-center gap-1 disabled:opacity-50">
            <RotateCcw className="w-3 h-3" /> Restore purchases
          </button>
          <Sparkles className="w-4 h-4 text-[#F4B333]" />
        </div>

        {/* Store-specific legal disclosure. iOS: Apple 3.1.2(c) — subscription
            title, length, price, EULA + Privacy. Android: Play Policy §4.5 —
            Play billing terms + no external payment links.
            DO NOT REMOVE. */}
        <div className="mt-4 pt-4 border-t border-[#E8E4DB] text-[10px] text-[#78716C] leading-relaxed space-y-2" data-testid="paywall-legal">
          {isAndroidNative() ? (
            <>
              <p>
                Payment will be charged to your Google Play account at confirmation of purchase.
                Your subscription automatically renews unless auto-renew is turned off at least
                <b> 24 hours before</b> the end of the current period. Your account will be charged
                for renewal within 24 hours prior to the end of the current period, at the same
                price shown above. You can manage and cancel your subscriptions by going to the
                <b> Google Play Store → Menu → Payments &amp; Subscriptions → Subscriptions</b> after purchase.
              </p>
              <p className="flex flex-wrap gap-x-3 gap-y-1 pt-1" data-testid="paywall-legal-links">
                <a href="https://play.google.com/about/play-terms/"
                   target="_blank" rel="noopener noreferrer"
                   data-testid="paywall-play-terms-link"
                   className="underline font-semibold text-[#57534E]">{t('googlePlayTerms')}</a>
                <span>·</span>
                <a href="/terms" target="_blank" rel="noopener noreferrer"
                   data-testid="paywall-app-terms-link"
                   className="underline font-semibold text-[#57534E]">{t('aayiAppTerms')}</a>
                <span>·</span>
                <a href="/privacy" target="_blank" rel="noopener noreferrer"
                   data-testid="paywall-privacy-link"
                   className="underline font-semibold text-[#57534E]">{t('privacyPolicy')}</a>
              </p>
            </>
          ) : (
            <>
              <p>
                Payment will be charged to your Apple ID account at confirmation of purchase.
                Your subscription automatically renews unless auto-renew is turned off at least
                <b> 24 hours before</b> the end of the current period. Your account will be charged
                for renewal within 24 hours prior to the end of the current period, at the same
                price shown above. You can manage and cancel your subscriptions by going to your
                Apple ID <b>Account Settings → Subscriptions</b> after purchase. Any unused portion
                of a free trial period, if offered, will be forfeited when you purchase a subscription.
              </p>
              <p className="flex flex-wrap gap-x-3 gap-y-1 pt-1" data-testid="paywall-legal-links">
                <a href="https://www.apple.com/legal/internet-services/itunes/dev/stdeula/"
                   target="_blank" rel="noopener noreferrer"
                   data-testid="paywall-eula-link"
                   className="underline font-semibold text-[#57534E]">Terms of Use (EULA)</a>
                <span>·</span>
                <a href="/terms" target="_blank" rel="noopener noreferrer"
                   data-testid="paywall-app-terms-link"
                   className="underline font-semibold text-[#57534E]">{t('aayiAppTerms')}</a>
                <span>·</span>
                <a href="/privacy" target="_blank" rel="noopener noreferrer"
                   data-testid="paywall-privacy-link"
                   className="underline font-semibold text-[#57534E]">{t('privacyPolicy')}</a>
              </p>
            </>
          )}
        </div>
      </motion.div>
    </div>
  );
};

export default NativePaywall;
